import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Share2, Eye, Clock, X, Loader2, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import logger from '../../lib/logger';

/**
 * Recently Shared Decks — surface every active Quarterly Deck
 * share link the user has minted, with live "opened N times"
 * counters so sales engineers can spot a hot prospect (CFO just
 * opened your deck twice in the last hour) and follow up while
 * the iron's hot.
 *
 * Data lives in `quarterly_deck_share_tokens`; the component
 * polls /shares on mount + every 60s. Each row exposes a Copy
 * (re-paste the link), Open (preview the public page in a new
 * tab), and Revoke action. Revoke is optimistic.
 */
function _formatRelative(iso) {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const diff = Date.now() - t;
    if (diff < 0) return 'just now';
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    const w = Math.floor(d / 7);
    return `${w}w ago`;
  } catch {
    return null;
  }
}

export function RecentlySharedDecksPanel() {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState({});
  const [copiedToken, setCopiedToken] = useState(null);

  const fetchShares = useCallback(async () => {
    try {
      const res = await api.get('/quarterly-deck/pins/shares');
      setShares(res.data?.shares || []);
    } catch (err) {
      logger.error?.('shares fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShares();
    // Only poll while the tab is visible. Background tabs don't
    // need a fresh "opened N times" count every 60s, and polling
    // in the background wastes API calls + battery on mobile.
    let id = null;
    const start = () => {
      if (id) return;
      id = setInterval(fetchShares, 60000);
    };
    const stop = () => {
      if (id) {
        clearInterval(id);
        id = null;
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Catch up on anything that happened while we were hidden
        fetchShares();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchShares]);

  const handleRevoke = async (token) => {
    if (revoking[token]) return;
    setRevoking((r) => ({ ...r, [token]: true }));
    // Optimistic remove
    const previous = shares;
    setShares((rows) => rows.filter((r) => r.token !== token));
    try {
      await api.delete(`/quarterly-deck/pins/shares/${token}`);
      toast.success('Share link revoked');
    } catch (err) {
      logger.error?.('revoke failed', err);
      setShares(previous);
      toast.error('Could not revoke — try again');
    } finally {
      setRevoking((r) => {
        const next = { ...r };
        delete next[token];
        return next;
      });
    }
  };

  const handleCopy = async (token) => {
    const absolute = `${window.location.origin}/q/${token}`;
    try {
      await navigator.clipboard.writeText(absolute);
      setCopiedToken(token);
      toast.success('Link copied');
      setTimeout(() => setCopiedToken((t) => (t === token ? null : t)), 2200);
    } catch {
      toast.error('Could not copy — select manually');
    }
  };

  // Hide the panel entirely when there's no data and we've
  // finished loading. Sales engineers who haven't shared yet
  // shouldn't see a confusingly empty card on the home page.
  if (loading) return null;
  if (!shares.length) return null;

  return (
    <Card className="border-slate-200" data-testid="recently-shared-decks-panel">
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-slate-900">
              Recently Shared Decks
            </h3>
            <span className="text-xs text-slate-400" data-testid="recently-shared-count">
              ({shares.length})
            </span>
          </div>
          <button
            type="button"
            onClick={fetchShares}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="recently-shared-refresh"
          >
            Refresh
          </button>
        </div>
        <ul className="divide-y divide-slate-100" data-testid="recently-shared-list">
          {shares.map((share) => {
            const recipient = share.recipient_label || 'Anyone with the link';
            const opens = share.view_count || 0;
            const lastView = _formatRelative(share.last_view_at);
            const isHot = opens > 0 && (() => {
              if (!share.last_view_at) return false;
              const diffMin = (Date.now() - new Date(share.last_view_at).getTime()) / 60000;
              return diffMin >= 0 && diffMin < 60;
            })();
            return (
              <li
                key={share.token}
                className="py-3 flex items-center gap-3"
                data-testid={`shared-deck-row-${share.token}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {recipient}
                    </p>
                    {isHot && (
                      <span
                        className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700"
                        data-testid="hot-prospect-badge"
                        title="Recipient opened the deck within the last hour — strike while it's hot"
                      >
                        Hot
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                    <span className="inline-flex items-center gap-1" data-testid={`shared-deck-opens-${share.token}`}>
                      <Eye className="h-3 w-3" />
                      Opened {opens}×
                    </span>
                    {lastView ? (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Last opened {lastView}
                      </span>
                    ) : (
                      <span className="text-slate-400 italic">Not opened yet</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleCopy(share.token)}
                    className="h-8 px-2 text-slate-500 hover:text-slate-900"
                    data-testid={`shared-deck-copy-${share.token}`}
                    title="Copy link"
                  >
                    {copiedToken === share.token ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <a
                    href={`/q/${share.token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-8 px-2 inline-flex items-center text-slate-500 hover:text-slate-900 rounded-md hover:bg-slate-100 transition-colors"
                    data-testid={`shared-deck-open-${share.token}`}
                    title="Open public preview in new tab"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRevoke(share.token)}
                    disabled={!!revoking[share.token]}
                    className="h-8 px-2 text-slate-500 hover:text-rose-600"
                    data-testid={`shared-deck-revoke-${share.token}`}
                    title="Revoke share link"
                  >
                    {revoking[share.token] ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <X className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

export default RecentlySharedDecksPanel;
